import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';

import { Sort } from '@angular/material/sort';

import { MatTableDataSource } from '@angular/material/table';

import { ActivatedRoute } from '@angular/router';

import { translate } from '@jsverse/transloco';
import { EnumErrorStates, ErrorObject } from '@inmoment/the-kitchen';

import { AppService } from 'app/app.service';
import { AuthService, Session } from 'app/providers/bridge-api/auth.service';
import {
  ConfigurationService,
  SemantriaConfiguration,
} from 'app/providers/semantria-api/configuration.service';
import { Analysis, AnalysisService } from 'app/providers/storage-api/analysis.service';
import { StorageAccountService } from 'app/providers/storage-api/blender-account.service';
import { CollectionService } from 'app/providers/storage-api/collection.service';
import {
  Calculation,
  CalculationListItem,
  PredictiveService,
} from 'app/providers/storage-api/predictive.service';
import { AlertService } from 'app/services/alert.service';
import {
  injectComponentTelemetryService,
  EnableComponentTelemetry,
  EnableClassTelemetry,
} from 'app/services/otel.service';

import { SystemService } from 'app/services/system.service';
import { GroupService } from '../../../../providers/semantria-api/group.service';
import { FieldService } from '../../../../providers/storage-api/field.service';
import { DownloadTasksService, TaskType } from '../../../../providers/tasks-api/download.service';
import { generalNameValidator } from '../../../../utils/customValidators';
import { ProjectCollection } from '../../models/project-collection.model';

const DEFAULT_REQUEUE_RATE_MINUTES = 15;

interface CalculationTableData extends CalculationListItem {
  dependentLabel: string;
  driversTooltip: string;
  hierarchyTooltip: string;
}

@EnableClassTelemetry()
@Component({
  selector: 'app-analysis-details',
  templateUrl: './analysis-details.component.html',
  styleUrls: ['./analysis-details.component.scss'],
  standalone: false,
})
export class AnalysisDetailsComponent implements AfterViewInit, OnInit, OnDestroy, OnChanges {
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();
  @Input() collections: ProjectCollection[];
  @Input() collectionName: string;
  @Input() configurations: Map<string, SemantriaConfiguration>;

  @Output() modalClosed: EventEmitter<any> = new EventEmitter();
  sort: Sort;

  displayName: string;
  isSaving = false;
  analysisDs: MatTableDataSource<Analysis>;
  private analysesRefreshTimeout;
  private session: Session;

  selectedAnalysis: Analysis;

  analysisColumns = ['displayName', 'analysisDate', 'configName', 'actions'];
  requeueRateMinutes: number;
  collectionNameStatus: ErrorObject = { state: EnumErrorStates.SUCCESS, text: '' };

  showDriverAnalysis = false;
  driverAnalysisColumns = [
    'displayName',
    'analysisDate',
    'dependent',
    'drivers',
    'breakdown',
    'actions',
  ];
  driverAnalysisDs: MatTableDataSource<CalculationTableData>;

  constructor(
    private collectionService: CollectionService,
    public configurationService: ConfigurationService,
    private alertService: AlertService,
    private authService: AuthService,
    private analysisService: AnalysisService,
    private downloadTaskService: DownloadTasksService,
    private groupService: GroupService,
    private route: ActivatedRoute,
    private predictiveService: PredictiveService,
    private accountService: StorageAccountService,
    private systemService: SystemService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.collections) {
      if (this.analysisDs) {
        this.analysisDs.data = this.analyses;
      }
    }
  }

  ngOnInit() {
    this.analysisDs = new MatTableDataSource(this.analyses);
    this.sort = {
      active: 'displayName',
      direction: 'asc',
    };
    this.displayName = this.collection.data.displayName;
    if (this.collection.data.isDriverAnalysis) {
      this.getCalculation();
    }
    const config = AppService.getConfig();
    this.requeueRateMinutes = config.limits.requeueRateMinutes || DEFAULT_REQUEUE_RATE_MINUTES;
  }

  async getCalculation() {
    const calculations = await this.predictiveService.listCalculations(
      this.collection.data.projectId,
      this.collection.data.collectionName,
      { pageSize: 1024 }
    );

    this.driverAnalysisDs = new MatTableDataSource(
      calculations.items.map((c) => ({
        ...c,
        dependentLabel: '',
        driversTooltip: '…',
        hierarchyTooltip: '…',
      }))
    );
    this.showDriverAnalysis = true;

    await this.accountService.groupFieldsSub.latest();

    for (const citem of this.driverAnalysisDs.data) {
      this.predictiveService
        .getCalculationById(
          this.collection.data.projectId,
          this.collection.data.collectionName,
          citem.id
        )
        .then((calculation) => {
          citem.dependentLabel = this.getDependentLabel(calculation);
          citem.driversTooltip = this.getDriversTooltip(calculation);
          citem.hierarchyTooltip = this.getHierarchyTooltip(calculation);
        });
    }
  }

  get collection(): ProjectCollection {
    return this.collections.find((c) => c.data.collectionName === this.collectionName);
  }

  get analyses(): Analysis[] {
    return this.collection.analyses;
  }

  async ngAfterViewInit() {
    this.session = await this.authService.getSession();
  }

  public onAnalysisDetailsTableSortChanged($event: Sort) {
    const sortData = this.setSortData($event);

    this.analysisDs.data.sort((u1, u2) => {
      switch (sortData.active) {
        case 'displayName':
          return this.propSort(u1.displayName, u2.displayName, sortData.direction);
        case 'analysisDate':
          return this.propSort(u1.modifiedAt, u2.modifiedAt, sortData.direction);
        case 'configName':
          return this.propSort(u1.configName, u2.configName, sortData.direction);
        default:
          return 0;
      }
    });

    this.analysisDs.data = [...this.analysisDs.data];
  }

  private propSort(a, b, dir) {
    if (typeof a === 'string') {
      return a.localeCompare(b) * dir;
    } else {
      return (a - b) * dir;
    }
  }

  private setSortData($event: Sort) {
    let direction: number;
    let active: string;
    this.sort = $event;

    if (this.sort?.direction) {
      direction = this.sort.direction === 'asc' ? 1 : -1;
    }
    if (this.sort?.active) {
      active = this.sort.active;
    }

    return {
      direction,
      active,
    };
  }

  public showRequeueButton(analysis: Analysis): boolean {
    if (!analysis.modifiedAt || analysis.status !== 'OPEN') {
      return false;
    }
    const now = new Date();
    const elapsedMinutes = (+now - +analysis.modifiedAt) / 60000;
    if (elapsedMinutes < this.requeueRateMinutes) {
      return false;
    }
    return true;
  }

  public async requeueAnalysis(analysis: Analysis) {
    try {
      await this.analysisService.requeueAnalysis(analysis);
      this.alertService.showToast(translate('analysis-details.analysis-requeue-success'));
      this.modalClosed.emit(null);
    } catch (error) {
      this.alertService.showErrorToast(
        translate('analysis-details.analysis-requeue-failed'),
        error
      );
    }
  }

  public showProgressSpinner(analysis: Analysis): boolean {
    return (
      analysis.status === 'OPEN' || analysis.status === 'QUEUED' || analysis.status === 'DELETING'
    );
  }

  public showDeleteAnalysis(analysis: Analysis): boolean {
    return (
      analysis.status !== 'OPEN' &&
      analysis.status !== 'QUEUED' &&
      analysis.status !== 'DELETING' &&
      analysis.status !== 'DELETED' &&
      !this.isReadOnly
    );
  }

  public showStopReanalyze(analysis: Analysis): boolean {
    return analysis.isReAnalyze && !this.isReadOnly;
  }

  public deleteAnalysis(analysis: Analysis): void {
    if (analysis.status === 'OPEN' || analysis.status === 'DELETING') {
      return;
    }

    this.modalClosed.emit({
      action: 'OPEN_DELETE_ANALYSIS_DIALOG',
      data: { collection: this.collection, analysis },
    });
  }

  public reAnalyzeAnalaysis(analysis: Analysis): void {
    if (analysis.status === 'OPEN' || analysis.status === 'DELETING') {
      return;
    }

    this.modalClosed.emit({
      action: 'OPEN_RE_ANALYSIS_DIALOG',
      data: { collection: this.collection, analysis },
    });
  }

  public stopReAnalyze(analysis: Analysis): void {
    this.modalClosed.emit({
      action: 'OPEN_STOP_RE_ANALYSIS_DIALOG',
      data: { collection: this.collection, analysis },
    });
  }

  public cancel(): void {
    this.modalClosed.emit(null);
  }

  public onValidateCollectionName() {
    const exsistingNames = this.collections.reduce((filtered: string[], projectCollection) => {
      if (projectCollection.data.collectionName !== this.collectionName) {
        filtered.push(projectCollection.data.displayName);
      }

      return filtered;
    }, []);

    generalNameValidator(
      this.displayName,
      this.collectionNameStatus,
      translate('analysis-details.collection'),
      64,
      true,
      exsistingNames,
      false
    );
  }

  public collectionNameHasErrors(): boolean {
    return (
      this.collectionNameStatus.state !== EnumErrorStates.SUCCESS ||
      this.displayName === this.collection.data.displayName
    );
  }

  public async saveCollectionName() {
    const collectionData = this.collection.data;
    if (!this.canCreateNewAnalysis() && !collectionData.isAutoAnalyze) {
      return;
    }
    this.displayName = this.displayName.trim();
    collectionData.displayName = this.displayName;
    this.isSaving = true;
    try {
      await this.collectionService.updateCollection(collectionData);
      this.alertService.showToast(translate('analysis-details.collection-save-success'));
    } catch (e) {
      this.alertService.showErrorToast(translate('analysis-details.collection-save-failed'), e);
    }
    this.isSaving = false;
  }

  public canCreateNewAnalysis(): boolean {
    const projectCollection = this.collection;
    return (
      !projectCollection.data.isAutoAnalyze &&
      !this.authService.isReadOnly &&
      !projectCollection.activeAnalysis &&
      projectCollection.analyses.length <= 10 &&
      projectCollection.data.documentCount > 0 &&
      !this.collection.data.locked
    );
  }

  public createNewAnalysis() {
    this.modalClosed.emit({
      action: 'OPEN_NEW_ANALYSIS_DIALOG',
    });
  }

  public showEditAnalysis(analysis: Analysis): boolean {
    return analysis.status === 'IDLE' && !this.isReadOnly;
  }

  public editAnalysis(analysis: Analysis) {
    this.modalClosed.emit({
      action: 'OPEN_NEW_ANALYSIS_DIALOG',
      analysis,
    });
  }

  public showDownloadAnalysisExcelReport(analysis: Analysis): boolean {
    return analysis.status === 'IDLE';
  }

  public downloadAnalysisExcelReport(analysis: Analysis, reportType: TaskType, fileNameSuffix) {
    this.modalClosed.emit();

    const groupId = this.groupService.getSelectedGroupId(true);
    const projectId = this.route.snapshot.params['projectId'];

    this.downloadTaskService
      .createTask({
        _type: reportType,
        groupId,
        format: 'EXCEL',
        projectId,
        filename: `${analysis.displayName}-${fileNameSuffix}`,
        source: 'Collection',
        size: 0,
        query: `(analyses.analysisName: "${analysis.name}" IN analyses)`,
        sortField: 'createdAt',
        sortOrder: 'DESC',
        collectionId: analysis.collectionName,
        analysisId: analysis.name,
      })
      .then((_result) => {
        this.alertService.showToast(
          translate('analysis-details.report-generating'),
          undefined,
          undefined,
          undefined,
          'open-download-center-pointer',
          'open-download-center'
        );
      })
      .catch((error) => {
        this.alertService.showErrorToast(
          translate('analysis-details.report-generate-failed'),
          error
        );
      });
  }

  showAnalysisError(analysis: Analysis) {
    return analysis.status === 'ERROR';
  }

  copyAnalysisError(analysis: Analysis) {
    this.systemService.copyToClipboard(analysis.statusText);

    this.alertService.showToast(translate('analysis-details.status-text-copied'));
  }

  ngOnDestroy() {
    clearInterval(this.analysesRefreshTimeout);
  }

  public get isReadOnly() {
    return this.authService.isReadOnly;
  }

  public deleteDriverAnalysis(element: CalculationTableData) {
    this.modalClosed.emit({
      action: 'OPEN_DELETE_DRIVER_ANALYSIS_DIALOG',
      data: {
        collection: this.collection,
        collectionName: this.collection.data.collectionName,
        projectId: this.collection.data.projectId,
        calculationId: element.id,
        displayName: element.displayName ?? '',
      },
    });
  }

  private getDriversTooltip(calculation: Calculation) {
    let ret = '';
    calculation.request.driverColumnLabels.forEach((value) => {
      ret += '- ' + FieldService.getFieldLongName(value) + '\n';
    });
    return ret;
  }

  private getHierarchyTooltip(calculation: Calculation) {
    let ret = '';

    if (calculation.result?.breakdown) {
      for (const key of Object.keys(calculation.result.breakdown)) {
        ret += '- ' + key + '\n';
      }
    }

    if (ret === '') {
      ret = translate('analysis-details.no-hierarchy');
    }
    return ret;
  }

  private getDependentLabel(calculation: Calculation): string {
    return FieldService.getFieldLongName(calculation.request.dependentColumnLabel);
  }

  get canExportQueryTopicsReport() {
    return this.selectedAnalysis?.topicTypes.includes('query');
  }

  get canExportConceptTopicsReport() {
    return this.selectedAnalysis?.topicTypes.includes('concept');
  }

  get canExportThemesReport() {
    return this.selectedAnalysis?.hasThemes;
  }

  get canExportEntitiesReport() {
    return this.selectedAnalysis?.hasEntities;
  }

  get canExportTaxonomyReport() {
    return this.selectedAnalysis?.hasTaxonomy;
  }
}
